/**
 * Date/time formatting helpers for audit-facing admin views.
 *
 * Two concerns this module addresses:
 *
 *  1. Audit timestamp stability — review item M1.
 *     `new Date(iso).toLocaleString()` renders in the viewer's local
 *     timezone, which makes regulator-facing screenshots unreliable
 *     (two AD users see two different strings for the same event). Use
 *     Intl.DateTimeFormat with `timeZone: 'UTC'` + `timeZoneName: 'short'`
 *     so the displayed value always matches the backing audit_log row.
 *     Ref: https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Intl/DateTimeFormat
 *
 *  2. Date-only day diff off-by-one — review item M2.
 *     ECMAScript parses `YYYY-MM-DD` as UTC midnight (the MDN "historical
 *     spec error" — https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Date/parse).
 *     Comparing that against `Date.now()` (local ms) in a non-UTC zone
 *     flips the day count the credential-expiry badge shows. Parse the
 *     Y/M/D components explicitly and compare UTC-day starts.
 */

// Build lazily — older Node + small-icu installs can throw during module
// load if the locale isn't bundled. Falls back to plain ISO truncation
// when Intl isn't usable.
let _auditFmt = null;
function auditFmt() {
  if (_auditFmt !== null) return _auditFmt;
  try {
    _auditFmt = new Intl.DateTimeFormat('en-US', {
      year: 'numeric',
      month: 'short',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      timeZone: 'UTC',
      timeZoneName: 'short',
      hour12: false,
    });
  } catch (_) {
    _auditFmt = false; // sentinel: Intl not available
  }
  return _auditFmt;
}

let _shortDateFmt = null;
function shortDateFmt() {
  if (_shortDateFmt !== null) return _shortDateFmt;
  try {
    _shortDateFmt = new Intl.DateTimeFormat('en-US', {
      year: 'numeric', month: 'short', day: '2-digit', timeZone: 'UTC',
    });
  } catch (_) {
    _shortDateFmt = false;
  }
  return _shortDateFmt;
}

/**
 * Render an ISO-8601 timestamp as a UTC-anchored audit label.
 * Returns '—' for null/undefined/unparseable input so callers don't
 * have to pepper ternaries everywhere.
 */
export function formatAuditTimestamp(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  const fmt = auditFmt();
  if (fmt) return fmt.format(d);
  // Intl unavailable — render the raw ISO minute, labeled as UTC.
  return d.toISOString().slice(0, 16).replace('T', ' ') + ' UTC';
}

/**
 * Render an ISO timestamp or `YYYY-MM-DD` as `Apr 26, 2026` (UTC-anchored).
 * Returns '—' for null/undefined/unparseable input.
 */
export function fmtDateShort(input) {
  if (!input) return '—';
  const s = String(input);
  // bare YYYY-MM-DD: parse explicit components to avoid local-tz drift
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(s);
  let d;
  if (m) {
    d = new Date(Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3])));
  } else {
    d = new Date(input);
  }
  if (Number.isNaN(d.getTime())) return '—';
  const fmt = shortDateFmt();
  if (fmt) return fmt.format(d);
  return d.toISOString().slice(0, 10);
}

/**
 * Days between today (UTC) and a date-only string `YYYY-MM-DD`.
 * Positive = future (days left), negative = past (days ago), null on
 * malformed input.
 *
 * Uses an explicit regex + Date.UTC so a stringy `'2026-04-22'` doesn't
 * silently become `2026-04-21` for a viewer west of UTC.
 */
export function daysUntilUtc(dateStr) {
  if (!dateStr) return null;
  const s = String(dateStr).slice(0, 10);
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(s);
  if (!m) return null;
  const y = Number(m[1]);
  const mo = Number(m[2]);
  const d = Number(m[3]);
  if (mo < 1 || mo > 12 || d < 1 || d > 31) return null;

  const targetUtcMs = Date.UTC(y, mo - 1, d);
  const MS_PER_DAY = 24 * 60 * 60 * 1000;
  const todayUtcMs = Math.floor(Date.now() / MS_PER_DAY) * MS_PER_DAY;
  return Math.floor((targetUtcMs - todayUtcMs) / MS_PER_DAY);
}
